import React, { useState } from 'react'
import { Controller, useFieldArray, useForm } from 'react-hook-form'
import { yupResolver } from '@hookform/resolvers/yup'
import * as yup from 'yup'
import {
  Button,
  Header,
  Text,
  Modal,
  Label,
  Dropdown,
  FieldGroup,
  Input,
  Link,
  Fields,
} from '@pet-notify/ui'

import vaultImage from './images/vault.jpg'
import tagFrontImage from './images/tag-front.jpg'
import tagBackImage from './images/tag-back.jpg'
import useDocumentTitle from 'hooks/document-title'
import ErrorMessage from 'components/ErrorMessage'
import { petTypes } from '@pet-notify/data'
import { IntellitagsInput } from 'services/orders'
import style from './style.module.css'

const schema = yup.object().shape({
  name: yup.string().required('Pet name is required'),
  type: yup.string().required('Pet type is required'),
  weight: yup.string().required('Pet weight is required'),
  size: yup.string().required('Tag size is required'),
  engraving: yup
    .string()
    .required('Engraving name is required')
    .when('size', {
      is: 'small',
      then: (schema) =>
        schema.max(
          MAX_CHARACTERS.small,
          `Name must not be over ${MAX_CHARACTERS.small} characters long`,
        ),
      otherwise: (schema) =>
        schema.max(
          MAX_CHARACTERS.large,
          `Name must not be over ${MAX_CHARACTERS.large} characters long`,
        ),
    }),
})

const SIZES = [
  { value: 'small', label: 'Small' },
  { value: 'medium', label: 'Medium' },
  { value: 'large', label: 'Large' },
]

const MAX_CHARACTERS = {
  small: 12,
  medium: 15,
  large: 15,
}

type Props = {
  onComplete: (values: IntellitagsInput) => void
  state?: Partial<IntellitagsInput>
}

function IntellitagsPage({ state, ...props }: Props) {
  const [isShowingFront, setIsShowingFront] = useState(true)
  const [showTagSizeModal, setShowTagSizeModal] = useState(false)
  useDocumentTitle('Customize your Intellitag | Pet Notify')

  const {
    register,
    control,
    handleSubmit,
    formState: { errors },
    watch,
  } = useForm<IntellitagsInput>({
    defaultValues: {
      intellitagOrderItems: state?.intellitagOrderItems || [
        {
          size: 'small' as const,
        },
      ],
    },
    resolver: yupResolver(
      yup.object().shape({
        intellitagOrderItems: yup.array().of(schema),
      }),
    ),
  })

  const collection = useFieldArray({
    control: control,
    name: 'intellitagOrderItems',
  })

  const intellitagOrderItems = watch('intellitagOrderItems')

  const onSubmit = handleSubmit(props.onComplete)

  return (
    <>
      <form onSubmit={onSubmit}>
        <Text variant='note' mb='2'>
          Step 2 of 4
        </Text>

        <Header mb='4'>Customize your Intellitag</Header>

        <div className={style.image}>
          <img src={vaultImage} alt='Pet Vault on iPhone and Intellitag' />
        </div>

        {collection.fields.map((item, index) => (
          <React.Fragment key={item.id}>
            <Header as='h2' size='large' mb='6'>
              Intellitag #{index + 1}
            </Header>

            <Fields>
              <FieldGroup>
                <Label htmlFor={`intellitagOrderItems.${index}.size`} mb='1'>
                  Size of tag
                </Label>
                <Text mb='3'>
                  <Link onClick={() => setShowTagSizeModal(true)}>
                    What size Intellitag should I get for my pet?
                  </Link>
                </Text>
                <Dropdown
                  options={SIZES}
                  id={`intellitagOrderItems.${index}.size`}
                  {...register(`intellitagOrderItems.${index}.size`)}
                />
              </FieldGroup>

              {errors?.intellitagOrderItems?.[index]?.size && (
                <ErrorMessage
                  error={errors?.intellitagOrderItems?.[index]?.size?.message}
                />
              )}

              <FieldGroup>
                <Label htmlFor={`intellitagOrderItems.${index}.type`}>
                  Pet type
                </Label>
                <Dropdown
                  options={petTypes}
                  id={`intellitagOrderItems.${index}.type`}
                  {...register(`intellitagOrderItems.${index}.type`)}
                />
              </FieldGroup>

              {errors?.intellitagOrderItems?.[index]?.type && (
                <ErrorMessage
                  error={errors?.intellitagOrderItems?.[index]?.type?.message}
                />
              )}

              <FieldGroup>
                <Label htmlFor={`intellitagOrderItems.${index}.weight`}>
                  Pet weight (in pounds)
                </Label>
                <Input
                  id={`intellitagOrderItems.${index}.weight`}
                  {...register(`intellitagOrderItems.${index}.weight`)}
                />
              </FieldGroup>

              {errors?.intellitagOrderItems?.[index]?.weight && (
                <ErrorMessage
                  error={errors?.intellitagOrderItems?.[index]?.weight?.message}
                />
              )}

              <FieldGroup>
                <Label htmlFor={`intellitagOrderItems.${index}.name`} mb='0'>
                  Pet name
                </Label>
                <Text mb='2' variant='note'>
                  This is the name reflected in your pet&apos;s digital vault.
                </Text>
                <Input
                  id={`intellitagOrderItems.${index}.name`}
                  {...register(`intellitagOrderItems.${index}.name`)}
                />
              </FieldGroup>

              {errors?.intellitagOrderItems?.[index]?.name && (
                <ErrorMessage
                  error={errors?.intellitagOrderItems?.[index]?.name?.message}
                />
              )}

              <FieldGroup>
                <Label
                  htmlFor={`intellitagOrderItems.${index}.engraving`}
                  mb='0'
                >
                  Intellitag engraving
                </Label>
                <Text mb='2' variant='note'>
                  What name do you want engraved on your Intellitag?{' '}
                  {intellitagOrderItems[index].size && (
                    <>
                      {MAX_CHARACTERS[intellitagOrderItems[index].size]}{' '}
                      character limit.
                    </>
                  )}
                </Text>
                <Controller
                  control={control}
                  name={`intellitagOrderItems.${index}.engraving`}
                  render={({ field }) => (
                    <Input
                      id={`intellitagOrderItems.${index}.engraving`}
                      {...field}
                      onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                        field.onChange(e.currentTarget.value.toUpperCase())
                      }
                    />
                  )}
                />
              </FieldGroup>

              {errors?.intellitagOrderItems?.[index]?.engraving && (
                <ErrorMessage
                  error={
                    errors?.intellitagOrderItems?.[index]?.engraving?.message
                  }
                />
              )}

              <FieldGroup>
                <Label htmlFor='phone' mb='0'>
                  Mobile phone number
                </Label>
                <Text mb='2' variant='note'>
                  This number is printed on your Intellitag and is optional
                  since your Intellitag ID will call, email, and text you when
                  someone finds your lost pet.
                </Text>
                <Input
                  type='phone'
                  placeholder='Optional'
                  id='phone'
                  {...register(`intellitagOrderItems.${index}.phone`)}
                />
              </FieldGroup>
            </Fields>
          </React.Fragment>
        ))}

        <Button type='submit' mb='6'>
          Continue
        </Button>

        <Text variant='note'>
          Your pet&apos;s Intellitag ID is directly tied to your Pet Notify user
          account and will not provide lost pet communications if gifted,
          distributed or transferred to any 3rd party.
        </Text>
      </form>

      {showTagSizeModal && (
        <Modal onClose={() => setShowTagSizeModal(false)}>
          <Header as='h3' size='xlarge' mb='6'>
            What size Intellitag should I get for my pet?
          </Header>
          <div className={style.image}>
            {isShowingFront ? (
              <img
                src={tagFrontImage}
                alt='Small, Medium and Large intellitags'
              />
            ) : (
              <img
                src={tagBackImage}
                alt='Small, Medium and Large intellitags'
              />
            )}
          </div>
          <div className={style.tagButtons}>
            <Button
              type='button'
              variant='hollow'
              onClick={() => setIsShowingFront((s) => !s)}
            >
              {isShowingFront
                ? 'Show Back of Intellitag'
                : 'Show Front of Intellitag'}
            </Button>
          </div>
          <Header size='medium' mb='2'>
            Small
          </Header>
          <Text mb='6'>
            .95&quot; diameter, recommended for pets &lt; 10lbs.
          </Text>
          <Header size='medium' mb='2'>
            Medium
          </Header>
          <Text mb='6'>
            1.10&quot; diameter, recommended for pets 10-25lbs.
          </Text>
          <Header size='medium' mb='2'>
            Large
          </Header>
          <Text mb='6'>1.25” diameter, recommended for pets &gt; 25lbs.</Text>

          <Button onClick={() => setShowTagSizeModal(false)}>Ok, got it</Button>
        </Modal>
      )}
    </>
  )
}

export default IntellitagsPage
